import { QueryClient, queryOptions, infiniteQueryOptions } from "@tanstack/react-query";
import {
  getFeaturedCollections,
  type FeaturedCollectionsParams,
  type FeaturedCollectionsResponse,
} from "@wtf/shared/api/featured";
import { batchNormalizeCollections } from "@wtf/shared";

export const featuredCollectionsOptions = (queryClient: QueryClient, params: FeaturedCollectionsParams = {}) =>
  queryOptions<FeaturedCollectionsResponse, Error>({
    queryKey: ["featured", "collections", params],
    queryFn: async () => {
      const result = await getFeaturedCollections(params);
      const normalizedCollections = batchNormalizeCollections(queryClient, result.items);
      normalizedCollections.forEach((normalizedCollection) => {
        queryClient.setQueryData(["collection", normalizedCollection.id], normalizedCollection);
      });
      return result;
    },
    staleTime: 5 * 60 * 1000,
  });

export const featuredCollectionsInfiniteOptions = (
  queryClient: QueryClient,
  params: Omit<FeaturedCollectionsParams, "offset"> = {}
) =>
  infiniteQueryOptions<FeaturedCollectionsResponse, Error>({
    queryKey: ["featured", "collections", "infinite", params],
    queryFn: async (context) => {
      const offset = context.pageParam as number;
      const requestParams = { limit: 30, ...params, offset };
      const result = await getFeaturedCollections(requestParams);
      const normalizedCollections = batchNormalizeCollections(queryClient, result.items);
      normalizedCollections.forEach((normalizedCollection) => {
        queryClient.setQueryData(["collection", normalizedCollection.id], normalizedCollection);
      });
      return result;
    },
    getNextPageParam: (lastPage: FeaturedCollectionsResponse): number | undefined => {
      return lastPage.hasMore ? lastPage.nextOffset : undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
  });
